import { IceManager } from './ice.manager';
import { ChannelManager } from './channel.manager';
import { NegotiationManager } from './negotiation.manager';
import { Stream } from '../stream';
import { randomUUID } from '@matap/shared';
import { registerWebrtcPolyfills } from '../../utils/polyfills';

export type Signaler = (sdp: object) => void;
export type SDPTransformer = (sdp: string) => string;

export class PeerConnection extends Stream<'data' | 'connect' | 'track' | 'stream' | 'error'> {
  private readonly peerConnection: RTCPeerConnection;
  private readonly senderMap = new Map<MediaStreamTrack, Map<MediaStream, RTCRtpSender & { removed?: boolean }>>();
  private readonly remoteTracks: { track: MediaStreamTrack; stream: MediaStream }[] = [];
  private readonly remoteStreams: MediaStream[] = [];
  private readonly signaler: Signaler;
  private readonly iceManager: IceManager;
  private readonly negotiationManager: NegotiationManager;
  private readonly channelManager: ChannelManager;

  private pcReady = false;
  private connected = false;
  private connecting = false;
  private channelReady = false;
  private isInitiator: boolean;

  private orphanedStream?: MediaStream;
  private chunkQueue: (string | ArrayBuffer | Blob | ArrayBufferView)[] = [];

  constructor({
    configuration,
    signaler,
    isInitiator,
    offerOptions,
    answerOptions,
    channelName,
    channelConfig,
    streams,
    sdpTransformer = (sdp: string) => sdp,
    trickle = true,
    allowHalfTrickle = false,
    channelNegotiated = false,
  }: {
    configuration: RTCConfiguration;
    signaler: Signaler;
    trickle?: boolean;
    streams: MediaStream[];
    allowHalfTrickle?: boolean;
    isInitiator: boolean;
    channelNegotiated?: boolean;
    channelName?: string;
    channelConfig?: RTCDataChannelInit;
    offerOptions: RTCOfferOptions;
    answerOptions?: RTCAnswerOptions;
    sdpTransformer?: SDPTransformer;
  }) {
    super();

    channelName = channelName || (isInitiator ? randomUUID() : undefined);
    this.isInitiator = isInitiator;
    this.signaler = signaler;

    registerWebrtcPolyfills();

    this.peerConnection = new RTCPeerConnection(configuration);
    this.iceManager = new IceManager(this.peerConnection, trickle, signaler);
    this.negotiationManager = new NegotiationManager(
      this.peerConnection,
      isInitiator,
      trickle,
      allowHalfTrickle,
      signaler,
      sdpTransformer,
      this.iceManager,
      offerOptions,
      answerOptions,
    );
    this.channelManager = new ChannelManager(this.peerConnection, isInitiator, channelNegotiated, channelName!, channelConfig);

    streams.forEach((stream) => this.addStream(stream));

    this.peerConnection.addEventListener('track', this.onTrack);

    this.iceManager
      .on('iceReady', () => {
        this.pcReady = true;
        this.maybeReady();
      })
      .on('error', (err) => this.emit('error', err))
      .on('close', () => this.destroy());

    this.negotiationManager
      .on('addTransceiver', (kind) => this.addTransceiver(kind))
      .on('error', (err) => this.emit('error', err))
      .on('close', () => this.destroy());

    this.channelManager
      .on('open', () => {
        if (this.isDestroyedOrDestroying()) return;
        this.channelReady = true;
        this.maybeReady();
      })
      .on('data', (args) => this.emit('data', args))
      .on('error', (err) => this.emit('error', err))
      .on('close', () => this.destroy());

    // @ts-ignore
    if (typeof this.peerConnection.peerIdentity === 'object') {
      // @ts-ignore
      this.peerConnection.peerIdentity.catch(() => this.destroy('ERR_PC_PEER_IDENTITY'));
    }

    this.negotiationManager.needsNegotiation();
  }

  public isConnected() {
    return this.connected && this.channelManager.isOpen();
  }

  public getPeerConnection() {
    return this.peerConnection;
  }

  public replaceTrack(oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack, stream: MediaStream) {
    if (this.isDestroyedOrDestroying()) return;

    const submap = this.senderMap.get(oldTrack);
    const sender = submap?.get(stream);

    if (!sender || !submap) {
      this.emit('error', 'Cannot replace track that was never added');
      return;
    }

    if (newTrack) {
      this.senderMap.set(newTrack, submap);
      this.senderMap.delete(oldTrack);
    }

    if (sender.replaceTrack) {
      sender.replaceTrack(newTrack).catch((err) => {
        if (newTrack) {
          this.senderMap.delete(newTrack);
          this.senderMap.set(oldTrack, submap);
        }
        this.emit('error', new Error(`Failed to replace track: ${err.message}`));
      });
    } else {
      this.emit('error', 'replaceTrack is not supported in this browser');
    }
  }

  public negotiate() {
    return this.negotiationManager.needsNegotiation();
  }

  public removeTrack(track: MediaStreamTrack, stream: MediaStream) {
    if (this.isDestroyedOrDestroying()) return;

    const submap = this.senderMap.get(track);
    const sender = submap ? submap.get(stream) : null;

    if (!sender) {
      this.emit('error', 'Cannot remove track that was never added');
      return;
    }

    try {
      sender.removed = true;
      this.peerConnection.removeTrack(sender);

      submap.delete(stream);
      if (submap.size === 0) {
        this.senderMap.delete(track);
      }
    } catch (err: any) {
      if (err.name === 'NS_ERROR_UNEXPECTED') {
        this.negotiationManager.removeTrackWhenReady(sender);
      } else {
        this.emit('error', err);
      }
    }
    this.negotiationManager.needsNegotiation();
  }

  public removeStream(stream: MediaStream) {
    if (this.isDestroyedOrDestroying()) return;
    stream.getTracks().forEach((track) => this.removeTrack(track, stream));
  }

  public onRemoteSignal(data: any) {
    if (this.isDestroyedOrDestroying()) return;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (err) {
        data = {};
      }
    }

    if (data.renegotiate && this.isInitiator) {
      this.negotiationManager.needsNegotiation();
    }
    if (data.transceiverRequest && this.isInitiator) {
      this.addTransceiver(data.transceiverRequest.kind, data.transceiverRequest.init);
    }
    if (data.candidate) {
      this.iceManager.onCandidateSignal(data.candidate);
    }
    if (data.sdp) {
      this.peerConnection
        .setRemoteDescription(new RTCSessionDescription(data))
        .then(() => {
          if (this.isDestroyedOrDestroying()) return;
          this.iceManager.onRemoteDescription();
          if (this.peerConnection.remoteDescription!.type === 'offer') {
            this.negotiationManager.createAnswer();
          }
        })
        .catch((err) => this.destroy(err));
    }
  }

  public addTransceiver(kind: 'audio' | 'video' | MediaStreamTrack, init?: RTCRtpTransceiverInit) {
    if (this.isDestroyedOrDestroying()) return;

    if (this.isInitiator) {
      try {
        this.peerConnection.addTransceiver(kind, init);
        this.negotiationManager.needsNegotiation();
      } catch (err) {
        this.emit('error', err);
      }
    } else {
      this.signaler({
        type: 'transceiverRequest',
        transceiverRequest: { kind, init },
      });
    }
  }

  public addStream(stream: MediaStream) {
    if (this.isDestroyedOrDestroying()) return;
    stream.getTracks().forEach((track) => this.addTrack(track, stream));
  }

  public addTrack(track: MediaStreamTrack, stream: MediaStream) {
    if (this.isDestroyedOrDestroying()) return;
    const submap = this.senderMap.get(track) || new Map();
    let sender = submap.get(stream);

    if (!sender) {
      sender = this.peerConnection.addTrack(track, stream) as RTCRtpSender & { removed?: boolean };
      submap.set(stream, sender);
      this.senderMap.set(track, submap);
      this.negotiationManager.needsNegotiation();
    } else if (sender.removed) {
      this.emit('error', 'Track has been removed. You should enable/disable tracks that you want to re-add');
    } else {
      this.emit('error', 'Track has already been added to that stream.');
    }
  }

  private onTrack = (event: RTCTrackEvent) => {
    if (this.isDestroyedOrDestroying()) return;

    let streams = event.streams;

    if (!streams || streams.length === 0) {
      if (!this.orphanedStream) this.orphanedStream = new MediaStream();
      this.orphanedStream.addTrack(event.track);
      streams = [this.orphanedStream];
    }

    streams.forEach((eventStream) => {
      this.emit('track', event.track, eventStream);
      this.remoteTracks.push({ track: event.track, stream: eventStream });

      if (this.remoteStreams.some((rs) => rs.id === eventStream.id)) return;

      this.remoteStreams.push(eventStream);
      queueMicrotask(() => this.emit('stream', eventStream));
    });
  };

  private maybeReady() {
    // 1. Guard against duplicate calls
    if (this.connected || this.connecting || !this.pcReady || !this.channelReady) return;

    // 2. Mark the connection as fully established instantly
    this.connecting = false;
    this.connected = true;

    // 3. Flush any pending data channel messages instantly
    if (this.chunkQueue.length > 0) {
      this.chunkQueue.forEach((chunk) => this.channelManager.send(chunk));
      this.chunkQueue = [];
    }

    // 4. Tell the UI we are connected instantly!
    // No more waiting for React Native to cross the bridge 20 times.
    this.emit('connect');
  }

  public sendChunk(data: string | ArrayBuffer | Blob | ArrayBufferView) {
    if (!this.isConnected()) {
      this.chunkQueue.push(data);
      return;
    }
    return this.channelManager.send(data);
  }

  protected onDestroy() {
    this.iceManager.destroy();
    this.negotiationManager.destroy();
    this.channelManager.destroy();
    this.connected = false;
    this.chunkQueue = [];
    this.orphanedStream = undefined;
    // this.remoteTracks.forEach((r) => r.track.stop());
    this.remoteTracks.length = 0;
    this.senderMap.clear();
    try {
      this.peerConnection.close();
    } catch (err) {}
    this.peerConnection.removeEventListener('track', this.onTrack);
  }
}
